import React, { useCallback, useMemo } from 'react';
import Tooltip from '../../../../../layout/ReusableComponent/Tooltip/Tooltip';

export function formatDiscussionHashtagTooltip(label) {
  const text = String(label || '').trim();
  if (!text) return '';
  return `Hashtag: #${text}`;
}

export function DiscussionHashtagTooltip({ label, children, position = 'top' }) {
  const tooltipText = formatDiscussionHashtagTooltip(label);
  if (!tooltipText) return children;

  return (
    <Tooltip text={tooltipText} position={position} className="support-open-disc-hashtag-tooltip">
      {children}
    </Tooltip>
  );
}

/** Match backend hashtag token rules (#ideas, #LaylaRhode, …). */
export const DISCUSSION_HASHTAG_TOKEN_RE = /#([a-zA-Z][a-zA-Z0-9_-]{0,63})/g;

/** Decode editor nbsp entities and collapse extra spaces for display. */
export function normalizeDiscussionDisplayText(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&#xA0;/gi, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+/g, ' ');
}

function normalizeDiscussionGapText(text) {
  const raw = String(text || '');
  if (!raw) return '';
  if (!raw.replace(/&nbsp;|&#160;|&#xA0;|\u00A0|[ \t]/gi, '').length) return ' ';
  return normalizeDiscussionDisplayText(raw);
}

const HASHTAG_BOX_BAR_HTML =
  '<span class="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--top" aria-hidden="true"></span>' +
  '<span class="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--bottom" aria-hidden="true"></span>' +
  '<span class="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--vl" aria-hidden="true"></span>' +
  '<span class="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--vr" aria-hidden="true"></span>';

function hashtagBoxInnerHtml(displayLabel, size = 'inline', { interactive = false, slug = '' } = {}) {
  const label = escapeHtml(displayLabel);
  const safeSlug = escapeHtml(slug);
  const safeDisplayLabel = escapeHtml(displayLabel);
  const className = `support-open-disc-hashtag-box support-open-disc-hashtag-box--${size}${
    interactive ? ' support-open-disc-hashtag-inline' : ''
  }`;
  const tooltipText = escapeHtml(formatDiscussionHashtagTooltip(displayLabel));
  const interactiveAttrs = interactive
    ? ` role="button" tabindex="0" title="${tooltipText}" data-hashtag-slug="${safeSlug}" data-hashtag-label="${safeDisplayLabel}"`
    : '';
  return (
    `<span class="${className}"${interactiveAttrs}>` +
    `<span class="support-open-disc-hashtag-box__grid">${HASHTAG_BOX_BAR_HTML}` +
    `<span class="support-open-disc-hashtag-box__cell"><span class="support-open-disc-hashtag-box__label">${label}</span></span>` +
    `</span></span>`
  );
}

function HashtagBoxGrid({ label }) {
  return (
    <span className="support-open-disc-hashtag-box__grid">
      <span className="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--top" aria-hidden />
      <span className="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--bottom" aria-hidden />
      <span className="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--vl" aria-hidden />
      <span className="support-open-disc-hashtag-box__bar support-open-disc-hashtag-box__bar--vr" aria-hidden />
      <span className="support-open-disc-hashtag-box__cell">
        <span className="support-open-disc-hashtag-box__label">{label}</span>
      </span>
    </span>
  );
}

/** Hashtag sign built from div bars with label in the center cell. */
export function DiscussionHashtagBox({ label, className = '', size = 'inline' }) {
  const safeLabel = String(label || '').trim();
  if (!safeLabel) return null;

  return (
    <span
      className={`support-open-disc-hashtag-box support-open-disc-hashtag-box--${size}${
        className ? ` ${className}` : ''
      }`}
    >
      <HashtagBoxGrid label={safeLabel} />
    </span>
  );
}

/** Pull `#tag` tokens from plain text for display when API rows are missing. */
export function extractDiscussionHashtagsFromText(text) {
  const normalized = normalizeDiscussionDisplayText(text);
  if (!normalized) return [];
  const list = [];
  const seen = new Set();
  const re = new RegExp(DISCUSSION_HASHTAG_TOKEN_RE.source, 'g');
  let match;
  while ((match = re.exec(normalized)) !== null) {
    const label = match[1];
    const slug = label.toLowerCase();
    if (seen.has(slug)) continue;
    seen.add(slug);
    list.push({ slug, label });
  }
  return list;
}

/** Remove inline `#tag` tokens from visible title text. */
export function stripDiscussionHashtagTokens(text) {
  return normalizeDiscussionDisplayText(text)
    .replace(DISCUSSION_HASHTAG_TOKEN_RE, '')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Feed excerpts: drop hashtag boxes and plain `#tag` tokens from rich HTML. */
export function stripDiscussionHashtagsFromHtml(html) {
  if (!html) return '';
  const withoutBoxes = String(html).replace(
    /<(div|span)[^>]*\bsupport-open-disc-hashtag-box\b[^>]*>[\s\S]*?<\/\1>/gi,
    ''
  );
  const parts = withoutBoxes.split(/(<[^>]+>)/g);
  return parts
    .map((part) => {
      if (!part || part.startsWith('<')) return part;
      return stripDiscussionHashtagTokens(part);
    })
    .join('');
}

/** API hashtags plus any `#tag` tokens still present in the title. */
export function resolveThreadDisplayHashtags(thread) {
  const slugMap = new Map();
  for (const h of Array.isArray(thread?.hashtags) ? thread.hashtags : []) {
    const slug = String(h?.slug || '').trim().toLowerCase();
    if (!slug) continue;
    const label = String(h?.label || slug).trim() || slug;
    slugMap.set(slug, { slug, label });
  }
  for (const h of extractDiscussionHashtagsFromText(thread?.title)) {
    if (!slugMap.has(h.slug)) slugMap.set(h.slug, h);
  }
  return [...slugMap.values()];
}

export function buildHashtagSlugLabelMap(hashtags) {
  const map = {};
  for (const h of Array.isArray(hashtags) ? hashtags : []) {
    const slug = String(h?.slug || '').trim().toLowerCase();
    const label = String(h?.label || slug).trim();
    if (slug) map[slug] = label;
  }
  return map;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Wrap #tokens in rich HTML with clickable inline hashtag buttons. */
export function linkifyDiscussionHashtagsInHtml(html, slugLabelMap = {}) {
  if (!html) return '';
  const parts = String(html).split(/(<[^>]+>)/g);
  return parts
    .map((part) => {
      if (!part || part.startsWith('<')) return part;
      const normalized = normalizeDiscussionDisplayText(part);
      return normalized.replace(DISCUSSION_HASHTAG_TOKEN_RE, (match, label) => {
        const slug = String(label || '').trim().toLowerCase();
        if (!slug) return match;
        const displayLabel = slugLabelMap[slug] || label;
        return hashtagBoxInnerHtml(displayLabel, 'inline', { interactive: true, slug });
      });
    })
    .join('');
}

export function DiscussionInlineHashtagText({ text, onHashtagClick, className = '' }) {
  const normalizedText = normalizeDiscussionDisplayText(text);
  if (!normalizedText) return null;

  const nodes = [];
  let lastIndex = 0;
  const re = new RegExp(DISCUSSION_HASHTAG_TOKEN_RE.source, 'g');
  let match;
  while ((match = re.exec(normalizedText)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(normalizeDiscussionGapText(normalizedText.slice(lastIndex, match.index)));
    }
    const label = match[1];
    const slug = label.toLowerCase();
    nodes.push(
      <DiscussionHashtagTooltip key={`${slug}-${match.index}`} label={label}>
        <span
          role="button"
          tabIndex={0}
          className="support-open-disc-hashtag-box support-open-disc-hashtag-box--inline support-open-disc-hashtag-inline"
          data-hashtag-slug={slug}
          data-hashtag-label={label}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onHashtagClick?.(slug, label);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            e.stopPropagation();
            onHashtagClick?.(slug, label);
          }}
        >
          <HashtagBoxGrid label={label} />
        </span>
      </DiscussionHashtagTooltip>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < normalizedText.length) {
    nodes.push(normalizeDiscussionGapText(normalizedText.slice(lastIndex)));
  }
  if (!nodes.length) return null;

  const Wrapper = className ? 'span' : React.Fragment;
  const wrapperProps = className ? { className } : undefined;

  if (nodes.length === 1 && typeof nodes[0] === 'string') {
    return className ? <span className={className}>{nodes[0]}</span> : nodes[0];
  }

  return <Wrapper {...wrapperProps}>{nodes}</Wrapper>;
}

export function useDiscussionHashtagInlineClick(onHashtagClick) {
  return useCallback(
    (e) => {
      const btn = e.target.closest?.(
        '.support-open-disc-hashtag-inline[data-hashtag-slug], .support-open-disc-hashtag-box[data-hashtag-slug]'
      );
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const slug = btn.getAttribute('data-hashtag-slug') || '';
      const label = btn.getAttribute('data-hashtag-label') || slug;
      if (slug) onHashtagClick?.(slug, label);
    },
    [onHashtagClick]
  );
}

export function useLinkifiedDiscussionBodyHtml(bodyHtml, knownHashtags) {
  const slugMap = useMemo(() => buildHashtagSlugLabelMap(knownHashtags), [knownHashtags]);
  return useMemo(
    () => linkifyDiscussionHashtagsInHtml(bodyHtml, slugMap),
    [bodyHtml, slugMap]
  );
}
